import { RadioGroup } from '@headlessui/react'
import {
    WeekView,
    Scheduler,
    TimeHeaderCell,
    TimeHeaderCellProps,
    DateHeaderCellProps,
    SchedulerItemProps,
    SchedulerItem,
    SchedulerItemContent,
} from '@progress/kendo-react-scheduler'
import GViewSlot from 'components/SchedulerSlot/view-slot'
import Translation from 'components/Translation'
import { useAppContext } from 'context/AppContext'
import { useAppErrorApiContext } from 'context/AppErrorContext'
import { AuthContext, useAuth } from 'context/AuthContext'
import { useTenantContext } from 'context/TenantContext'
import QRCode from 'qrcode.react'
import { useEffect, useState } from 'react'
import { CalendarApiItem, CalendarSlotItem, mapCalendarSlotFromDTO } from 'types/calendar'
import { LocationItem } from 'types/location'
import { classNames } from 'utils/dom-helpers'
import { getApiRequest } from 'utils/fetch-helper'
import { getTranslation } from 'utils/language-helper'
import { getCalendarWeek } from 'utils/scheduling-helper'
import { toHour, toYmd } from 'utils/string-localization-helper'
import { createModal } from '../ModalFactory'
import { ModalWrapper } from '../ModalWrapper'
import getConfig from 'next/config'

const ModalProvider = createModal(
    AuthContext,
    'PrintPreviewModal',
    () => <></>,
    () => <></>
)

function TimeCell(p: TimeHeaderCellProps) {
    return (
        <div className='text-[5px]'>
            <TimeHeaderCell {...p} format="ha" />
        </div>
    )
}

enum WEEK_TYPE {
    THIS_WEEK = "calendar.this_week",
    NEXT_WEEK = "calendar.next_week"
}

enum SERVICE_TYPE {
    ALL = "calendar.all",
    CLASSES = "calendar.classes",
    APPOINTMENTS = "calendar.appointments"
}

const DEFAULT_DAY_START = "08:00"
const DEFAULT_DAY_END = "18:00"

export default function PrintPreviewModal() {
    const { translations, locale, setGlobalLoading } = useAppContext()
    const { setGlobalError } = useAppErrorApiContext()
    const { PrintPreviewModal, CalendarPrintModal } = useAuth()
    const { attributes, setAttributes } = PrintPreviewModal
    const {
        tenant,
        default_booking_url,
        primaryColor,
        calendar_settings,
        services
    } = useTenantContext()
    const [week_type, setWeekType] = useState<WEEK_TYPE>(WEEK_TYPE.THIS_WEEK)
    const [items, setItems] = useState<CalendarSlotItem[]>([])
    const [type_filter, setTypeFilter] = useState<SERVICE_TYPE>(SERVICE_TYPE.ALL)
    const [day_start, setDayStart] = useState<string>(DEFAULT_DAY_START)
    const [day_end, setDayEnd] = useState<string>(DEFAULT_DAY_END)
    const [this_week_start, this_week_end] = getCalendarWeek(calendar_settings.start_day)
    const [next_week_start, next_week_end] = getCalendarWeek(calendar_settings.start_day, new Date(this_week_start.getTime() + (7 * 24 * 60 * 60 * 1000)))
    const [hide_month_day, toggleMonthDay] = useState<boolean>(false)
    // const [language, setLanguage] = useState<Record<string,string>>(DEFAULT_LANGUAGE_RECORD)
    const { NEXT_PUBLIC_STATIC_DOMAIN } = getConfig().publicRuntimeConfig

    function handleClose() {
        setAttributes({})
        PrintPreviewModal.close()
    }

    function DateCell(p: DateHeaderCellProps) {
        return (
            <div
                {...p}
                className={classNames(
                    p.className || '',
                    'border-l-white px-0.5 border-r-none'
                )}
            >
                <div className="text-left w-full">
                    <div className="block font-thin border-b border-gray-300 text-[5px] py-1.5">
                        {p.date.toLocaleDateString('default', {
                            weekday: 'long',
                        })}
                    </div>
                    {hide_month_day
                        ?
                        <div className='h-5' />
                        :
                        <strong className="text-[11px] mt-2">
                            {p.date.toLocaleDateString('default', {
                                day: '2-digit',
                            })}
                        </strong>
                    }

                </div>
            </div>
        )
    }

    function SlotItem(props: SchedulerItemProps) {
        return (
            <SchedulerItem
                {...props}
                className={classNames(
                    props.className || '',
                    'g-view-item rounded bg-green'
                )}
                style={{
                    backgroundColor: props.dataItem.primary_hex || ""
                }}
            >
                <SchedulerItemContent>
                    <div
                        title={props.dataItem?.service_name || ''}
                        className="flex flex-col truncate text-ellipsis z-10 leading-tight cursor-pointer text-[4.5px] p-0.5"
                    >
                        <div className="flex flex-wrap justify-between mb-0.5 font-bold">
                            <span
                                className="inline-block truncate"
                                title={props.dataItem?.service_name}
                            >
                                {props.dataItem?.service_name}
                            </span>
                        </div>
                        <span
                            className="inline-block truncate"
                        >
                            {toHour(props.dataItem?.start)} - {toHour(props.dataItem?.end)}
                        </span>
                        <div
                            className="inline-block truncate items-center"
                            title={props.dataItem?.staff_name}
                        >
                            <i className="feather-user mr-0.5" />
                            {props.dataItem?.staff_name}
                        </div>
                    </div>
                </SchedulerItemContent>
            </SchedulerItem>
        )
    }

    function fetchCalendarSlots(location_id: string, type_filter: SERVICE_TYPE, week_type: WEEK_TYPE) {
        try {
            setGlobalLoading(true, true)
            let appts, group_classes
            const from_ymd = week_type == WEEK_TYPE.THIS_WEEK ? toYmd(this_week_start) : toYmd(next_week_start)
            const to_ymd = week_type == WEEK_TYPE.THIS_WEEK ? toYmd(this_week_end) : toYmd(next_week_end)
            if (type_filter == SERVICE_TYPE.ALL || type_filter == SERVICE_TYPE.APPOINTMENTS) {
                appts = getApiRequest(
                    `/v1/bookings/appointments-by-tenant-id/?from=${from_ymd}&to=${to_ymd}&tenantId=${tenant?.id}`
                )
            }
            if (type_filter == SERVICE_TYPE.ALL || type_filter == SERVICE_TYPE.CLASSES) {
                group_classes = getApiRequest(
                    `/v1/offerings/group-class/?isRestricted=false&from=${from_ymd}&to=${to_ymd}&locationId=${location_id}`
                )
            }
            const promises = Promise.all([appts, group_classes])
            promises.then(async (results) => {
                const appt_classes = results
                    ?.shift()
                    ?.filter((appt: Record<string, any>) => appt.locationId === location_id)
                    ?.map(async (appt: Record<string, any>) => {
                        const svc = services?.find(
                            ({ id }) => id === appt.serviceId
                        )

                        const { attendees } = appt
                        let vals = attendees?.map(
                            async (item: string) =>
                                getApiRequest(`/v1/clients/${item}`)
                        )
                        const res = await Promise.all(vals)
                        const allResolved = res.every(
                            (item) => !!item?.user
                        )

                        return {
                            ...svc,
                            ...appt,
                            attendees: allResolved
                                ? res.map((item, index) => ({
                                    id: attendees[index],
                                    name: `${item?.user?.firstName} ${item?.user?.lastName}`,
                                    user: item?.user,
                                }))
                                : [
                                    {
                                        id: appt?.client?.user?.id,
                                        name: `${appt?.client?.user?.firstName} ${appt?.client?.user?.lastName}`,
                                    },
                                ],
                            order: appt?.orderDto,
                            user: appt?.client?.user,
                        } as unknown as CalendarApiItem
                    })
                const group_classes = results.flatMap((gc) => gc)
                const appointments = await Promise?.all(appt_classes || [])
                let new_items: CalendarSlotItem[] = []
                if ((appointments.length > 0 && appointments[0])
                    || (group_classes.length > 0 && group_classes[0])) {
                    const cal_items = [...appointments, ...group_classes]
                    new_items = cal_items.filter((cal_item) => cal_item && !cal_item.deletedAt).map((cal_item) => mapCalendarSlotFromDTO(cal_item))
                }
                setItems(new_items)
            })
        } catch (error) {
            setGlobalError(error)
        } finally {
            setGlobalLoading(false, true)
        }
    }

    useEffect(() => {
        let location: LocationItem | undefined = attributes?.location as LocationItem
        if (location?.id) {
            fetchCalendarSlots(location.id, type_filter, week_type)
        }
    }, [type_filter, week_type, attributes?.location])

    useEffect(() => {
        if (items.length > 0) {
            let start_hour = 24, end_hour = 0
            items.forEach((item) => {
                if (item.start.getHours() < start_hour) {
                    start_hour = item.start.getHours()
                }
                if (item.end.getHours() > end_hour) {
                    end_hour = item.end.getHours()
                }
            })

            if (start_hour >= 8 && end_hour <= 18) {
                setDayStart(DEFAULT_DAY_START)
                setDayEnd(DEFAULT_DAY_END)
                return
            }

            let start_offset = start_hour + 10
            if (start_offset > 23) {
                let end_offset = end_hour - 10
                if (end_offset >= 0) {
                    start_hour = end_offset
                }
            } else if (start_offset > end_hour) {
                end_hour = start_offset
            }
            setDayStart((start_hour < 10 ? "0" : "") + start_hour + ":00")
            setDayEnd((end_hour < 10 ? "0" : "") + end_hour + ":00")
        } else {
            setDayStart(DEFAULT_DAY_START)
            setDayEnd(DEFAULT_DAY_END)
        }
    }, [items])

    return (
        <ModalProvider.Visible>
            <ModalWrapper rootClassName="overflow-hidden" className="overflow-hidden">
                <div className="flex flex-row w-[1000px] bg-white shadow-2xl overflow-hidden rounded-md relative h-[500px] my-auto">
                    <div
                        className='flex flex-row w-3/5 bg-[#E6E7E8]/50 px-10 py-[70px]'
                        style={{
                            printColorAdjust: "exact"
                        }}
                    >
                        <div className='flex flex-col py-4 w-1/6 text-white relative shrink-0'
                            style={{
                                backgroundColor: primaryColor
                            }}
                        >
                            <div className="border-l-2 border-white px-3">
                                <div className="font-display text-[10px] font-[450]">
                                    {tenant && tenant.name}
                                </div>
                                {attributes?.location &&
                                    <small className="block mt-4 text-[6px] font-[450]">
                                        {(attributes.location as LocationItem).name}
                                    </small>
                                }
                            </div>
                            <div className="items-center text-center w-full mt-2.5">
                                <QRCode
                                    value={default_booking_url}
                                    className="w-[42px] h-[42px] mx-auto p-1 bg-white"
                                />
                            </div>
                            <Translation
                                content_key='calendar.weekly_schedule'
                                render_as='div'
                                className='font-[450] text-xl rotate-180 mt-auto'
                                style={{
                                    writingMode: 'vertical-rl'
                                }}
                            />
                            <img
                                src={`https://${NEXT_PUBLIC_STATIC_DOMAIN}/images/crescent_vector.svg`}
                                className='absolute bottom-0 left-0'
                            />
                        </div>
                        <div className='flex flex-col w-full h-full overflow-hidden bg-white p-1.5'>
                            <div className='flex flex-row justify-between'>
                                <div className='flex flex-col ml-6'>
                                    <div className='text-[5px] text-[#857F7F] font-[450] leading-[7px] my-auto'>
                                        <Translation content_key='calendar.as_of' />
                                        &nbsp;
                                        {new Date().toLocaleDateString()}
                                    </div>
                                    <div hidden={hide_month_day} className='text-[11px] text-neutral-900 font-bold leading-[13px]'>
                                        {week_type == WEEK_TYPE.THIS_WEEK ? this_week_start.toLocaleString(locale, { month: "long" }) : next_week_start.toLocaleString(locale, { month: "long" })}
                                    </div>
                                </div>
                                <img src={tenant?.bizLogoUrl || `https://${NEXT_PUBLIC_STATIC_DOMAIN}/images/aot_logo.svg`} className="w-5 h-5" />
                            </div>
                            < Scheduler
                                data={items}
                                view="WEEK"
                                header={() => <></>}
                                footer={() => <></>}
                                className="border-0 bg-white"
                                height="100%"
                                date={week_type == WEEK_TYPE.THIS_WEEK ? this_week_start : next_week_start}
                                editable={false}
                                viewSlot={(props) => {
                                    return (
                                        <span
                                            className='w-full border overflow-hidden text-[5px] bg-white'
                                        >
                                            {GViewSlot(props)}
                                        </span>
                                    )
                                }}
                                item={SlotItem}
                            >
                                <WeekView
                                    slotDivisions={1}
                                    slotDuration={60}
                                    dateHeaderCell={DateCell}
                                    timeHeaderCell={TimeCell}
                                    workDayStart={day_start}
                                    workDayEnd={day_end}
                                    className="calendar-widget"
                                    currentTimeMarker={false}
                                />
                            </Scheduler>
                            <Translation
                                content_key='calendar.class_schedule_subject_to_change'
                                render_as='div'
                                className='w-full justify-center leading-[6px] text-[5px] font-[450] text-[#857F7F] text-center'
                            />
                        </div>
                    </div>
                    <div className='flex flex-col w-2/5 px-8 py-[86px]'>
                        <Translation
                            content_key="calendar.print_preview"
                            render_as='h2'
                            className='font-medium text-xl text-primary mb-6'
                        />
                        <div className='w-full mb-7'>
                            <RadioGroup
                                value={type_filter}
                                onChange={(v: SERVICE_TYPE) => {
                                    setTypeFilter(v)
                                }}
                            >
                                <div className={'flex flex-row gap-x-5 w-full'}>
                                    {Object.values(SERVICE_TYPE).map((service_type: SERVICE_TYPE) => {
                                        return (
                                            <RadioGroup.Option key={service_type} value={service_type}>
                                                {({ checked }) => (
                                                    <div className="flex flex-row items-center gap-3 text-base cursor-pointer">
                                                        <div
                                                            className='rounded-full w-6 h-6 border-2 flex items-center'
                                                            style={{
                                                                borderColor: checked ? "#23476B" : "#BCBEC0"
                                                            }}
                                                        >
                                                            <i
                                                                className='rounded-full w-4 h-4 block m-auto'
                                                                style={{
                                                                    backgroundColor: checked ? "#23476B" : ""
                                                                }}
                                                            />
                                                        </div>
                                                        <Translation
                                                            content_key={service_type}
                                                            render_as="label"
                                                        />
                                                    </div>
                                                )}
                                            </RadioGroup.Option>
                                        )
                                    })}
                                </div>
                            </RadioGroup>
                        </div>
                        <div className='flex flex-col font-[450] text-[#212429] gap-y-1.5'>
                            <Translation
                                content_key='week'
                                render_as='label'
                            />
                            <select
                                value={week_type}
                                onChange={(e) => {
                                    setWeekType(e.target.value as WEEK_TYPE)
                                }}
                                className="rounded-lg border border-gray-four"
                            >
                                {Object.values(WEEK_TYPE).map((week_type) => {
                                    return (
                                        <option key={week_type} value={week_type}>
                                            {getTranslation(week_type, translations)}
                                        </option>
                                    )
                                })}
                            </select>
                        </div>
                        {/* <div className='flex flex-row mt-7'>
                            <Translation
                                content_key='language'
                                render_as='label'
                                className='text-[#212429] font-[450]'
                            />
                        </div> */}
                        <div className='flex flex-row mt-6 font-[450] items-center text-neutral-700'>
                            <input
                                type='checkbox'
                                className="h-6 w-6 border-gray-300 rounded text-white checked:bg-secondary-blue mr-4"
                                checked={hide_month_day}
                                onChange={() => {
                                    toggleMonthDay(!hide_month_day)
                                }}
                            />
                            <Translation
                                content_key='calendar.hide_month_and_date'
                            />
                        </div>
                        <div className='flex flex-row gap-x-6 mt-auto'>
                            <button
                                type="button"
                                className="flex border border-gray-200 text-gray-500 rounded-lg py-3 w-full justify-center"
                                onClick={() => {
                                    handleClose()
                                }}
                            >
                                <Translation
                                    content_key="cancel"
                                />
                            </button>
                            <button
                                disabled={items.length == 0}
                                type="button"
                                className="flex text-white bg-primary rounded-lg py-3 w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={() => {
                                    CalendarPrintModal.setAttributes({
                                        items,
                                        date: week_type == WEEK_TYPE.THIS_WEEK ? this_week_start : next_week_start,
                                        location: attributes?.location as LocationItem,
                                        day_start,
                                        day_end,
                                        hide_month_day: hide_month_day
                                    })

                                    CalendarPrintModal.open()
                                }}
                            >
                                <Translation
                                    content_key="calendar.print"
                                />
                            </button>
                        </div>
                    </div>
                </div>
            </ModalWrapper>
        </ModalProvider.Visible >
    )
}
